import * as yup from 'yup';
import { ChevronRight, ExternalLink, User } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Permission } from '__generated__/graphql';

import { Event } from './helpers';
import { getServerErrors } from '../../../../common/helpers';
import ProtectedSection from '../../../Common/ProtectedSection';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

export interface EditEventInterestListFormProps {
  event: Event;
}

const EditEventInterestListForm = ({ event }: EditEventInterestListFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();

  const getValues = (event: Event) => ({
    enabled: event.enable_interest_list,
  });

  const form = useForm({
    values: getValues(event),
  });

  const editEventInterestListSchema = yup.object().shape({
    enabled: yup.boolean(),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      interest_list: {
        ...form.values,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventInterestListSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'interest_list'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('interest_list')}
          </UI.Legend>
          <UI.InputDescription>
            {t('interest_list_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.enabled, touched: form.touched.enabled }}>
          <UI.FormGrid>
            <UI.Checkbox
              {...form.checkbox('enabled')}
              sc={{ box: true }}
              id="Enabled"
            >
              <UI.Strong sc={{ block: true }}>
                {t('enable_checkout_interest_list')}
              </UI.Strong>
              {t('enable_checkout_interest_list_description')}
            </UI.Checkbox>
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.Info>
          <UI.Span sc={{ mr: 3 }}>
            <UI.Icon>
              <User />
            </UI.Icon>
            {' '}
            {t('n_opt_ins_collected', { count: event.interest_list?.opt_in_count || 0,
              value: formatNumber(event.interest_list?.opt_in_count || 0) },
            )}
          </UI.Span>
          <ProtectedSection permissions={[Permission.view_registrations]}>
            <UI.Link
              to={`/${project.id}/participants/contacts?event=${event.id}&mail_opt_ins[]=${event.interest_list?.id}`}
              sc={{ noWrap: true, mr: 3, disabled: !event.interest_list }}
            >
              {t('view_opt_ins')}
              {' '}
              <UI.Icon>
                <ChevronRight />
              </UI.Icon>
            </UI.Link>
          </ProtectedSection>
          <ProtectedSection permissions={[Permission.messages]}>
            <UI.Link
              to={`/${project.id}/communication/permissions/${event.interest_list?.id}`}
              sc={{ noWrap: true, mr: 3, disabled: !event.interest_list }}
            >
              {t('configure_mail_permission')}
              {' '}
              <UI.Icon>
                <ChevronRight />
              </UI.Icon>
            </UI.Link>
          </ProtectedSection>
          <UI.A
            href={`/e/${event.id}/interest`}
            sc={{ noWrap: true, disabled: !event.interest_list }}
          >
            {t('view_form')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.A>
        </UI.Info>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventInterestListForm;
